import type { IndividualWarehouseUnitDto } from '../../api';
import { formatProductCodeUniqueForDisplay } from '../formatProductCodeUniqueDisplay';

export type InventoryRecordPieceDetailRow = {
  key: string;
  productCodeUnique: string;
  productName: string;
  productType: string;
  status: string;
  materialLabel: string;
};

type WarehouseProductLike = {
  id?: number;
  inventoryRecordItemId?: number | null;
  productCodeUnique?: string;
  productName?: string;
  productType?: string;
  status?: string;
  material?: { name?: string; id?: number | string } | string;
};

type RecordItemLike = {
  id?: number | string;
  quantity?: number;
  productId?: number;
  product?: {
    name?: string;
    code?: string;
    category?: { name?: string };
    material?: { name?: string };
  };
  individualUnits?: IndividualWarehouseUnitDto[];
};

function materialLabelFromWarehouse(
  material: WarehouseProductLike['material'],
  fallback?: string,
): string {
  if (material == null) {
    return fallback ?? 'N/A';
  }
  if (typeof material === 'string') {
    return material.trim() !== '' ? material : (fallback ?? 'N/A');
  }
  return material.name?.trim() || String(material.id ?? '') || fallback || 'N/A';
}

export function resolvePieceDetailsForItem(
  item: RecordItemLike,
  warehouseProducts: WarehouseProductLike[],
): InventoryRecordPieceDetailRow[] {
  if (warehouseProducts.length > 0) {
    return warehouseProducts.map((wp, index) => ({
      key: String(wp.id ?? `wp-${index}`),
      productCodeUnique: formatProductCodeUniqueForDisplay(wp.productCodeUnique, {
        status: wp.status,
      }),
      productName: wp.productName ?? item.product?.name ?? '—',
      productType: wp.productType ?? item.product?.category?.name ?? '—',
      status: wp.status ?? 'UNKNOWN',
      materialLabel: materialLabelFromWarehouse(wp.material, item.product?.material?.name),
    }));
  }

  const units = item.individualUnits ?? [];
  if (units.length > 0) {
    return units.map((unit, index) => ({
      key: `unit-${item.id ?? index}-${index}`,
      productCodeUnique: '—',
      productName: item.product?.name ?? '—',
      productType: item.product?.category?.name ?? '—',
      status: 'PENDING',
      materialLabel: item.product?.material?.name ?? 'N/A',
    }));
  }

  const qty = Number(item.quantity ?? 0);
  if (qty === 1 && item.product?.name != null && item.product.name.trim() !== '') {
    return [
      {
        key: `single-${item.id ?? item.productId ?? 0}`,
        productCodeUnique: '—',
        productName: item.product.name,
        productType: item.product?.category?.name ?? '—',
        status: 'PENDING',
        materialLabel: item.product?.material?.name ?? 'N/A',
      },
    ];
  }

  return [];
}

export function collectAutoExpandedProductIds(
  items: RecordItemLike[],
  warehouseProductsByItemId: Map<number, WarehouseProductLike[]>,
): Set<number> {
  const expanded = new Set<number>();
  for (const item of items) {
    const rawId = item.id;
    const itemId =
      typeof rawId === 'number'
        ? rawId
        : rawId != null && String(rawId).trim() !== ''
          ? Number(rawId)
          : NaN;
    if (!Number.isFinite(itemId)) continue;
    const warehouseProducts = warehouseProductsByItemId.get(itemId) ?? [];
    const details = resolvePieceDetailsForItem(item, warehouseProducts);
    if (Number(item.quantity ?? 0) === 1 && details.length > 0) {
      expanded.add(itemId);
    }
  }
  return expanded;
}
